// @ts-nocheck
import { cloneDeep, groupBy, uniq } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Tabs, useDidUpdate } from 'sonata-react-components';
import { isDOOHCampaign } from 'sonata-static-storage/lib/helpers/campaign.helpers';
import { normalize, normalizeWithUnit } from 'sonata-static-storage/lib/helpers/i18nUnits.helpers';
import { checkUserResourcePermissions } from 'sonata-static-storage/lib/helpers/user.helpers';
import { DWELL_TIME_ACC_KEYS } from 'sonata-static-storage/lib/samples/statsPreferences.samples';

import {
  formatNumber,
  formatTimeFrame,
  getCartographyFilter,
  getDateFormatted,
  getDefaultReportingCharts
} from '@/helpers/reporting.helpers';
import { abbreviateNumber } from '@/services/helpers';
import { reportingProgressSetStats, reportingTrigger } from '@/store/actions/reporting.actions';
import { getBaseCurrency, getBaseCurrencyName } from '@/store/selectors/baseCurrency.selectors';
import {
  getAggregatedStats,
  getChangeLogData,
  getHasAppsInstall,
  getHasAudio,
  getHasBaseline,
  getHasDigitalConversions,
  getHasFootfallAttribution,
  getHasLumenAttention,
  getHasVideo,
  getHasViewability,
  getIsAgeAndGenderVisible,
  getReportingProgressStats,
  getReportingType,
  getSelectedCampaigns,
  getStaticMode,
  includeHouseHoldImpacts
} from '@/store/selectors/reporting.selectors';
import {
  getCartographyCountries,
  getCartographyLevel,
  getCartographyLevelIds,
  getChartTimeStep,
  getCurrency,
  getTimeFrame,
  getTimeZone,
  getVariableType,
  getVariableValues
} from '@/store/selectors/reportingAdvancedFilter.selectors';
import { getUserRoles } from '@/store/selectors/resources.selectors';
import { getCurrentUser } from '@/store/selectors/user.selectors';
import { ImpressionsKpisChart } from '@/ts-modules/reporting/Progress/ImpressionsKpisChart';
import { ReportingAgeAndGender } from '@/ts-modules/reporting/Progress/ReportingAgeAndGender';
import { ReportingChart } from '@/ts-modules/reporting/Progress/ReportingChart';
import { SectionHeader } from '@/ts-modules/reporting/SectionHeader';
import {
  DWELL_TIME_KEYS,
  fetchProgressStats,
  getProgressStatsRequestBody
} from '@/ts-resources/reporting/reportingProgress.resources';

import type { TabsItemProps } from 'sonata-react-components';

function getTooltipConfig(currencyInfo: Record<string, any>, userCountry: Record<string, any>) {
  return {
    defaultFormatter(this: any) {
      if (this.series.userOptions.tooltip) {
        if (this.series.userOptions.tooltip.className === 'currency') {
          return `${this.series.name}: ${normalizeWithUnit(this.y, {
            ...currencyInfo,
            countries: [userCountry]
          })}`;
        }
        if (this.series.userOptions.tooltip.className === 'percent') {
          return `${this.series.name}: ${normalize(this.y, 2, userCountry)}%`;
        }
      }
      return `${this.series.name}: ${abbreviateNumber(parseInt(this.y))}`;
    }
  };
}

function getOutputMetricsForSelectedChart(chart: Record<string, any>, selectedChart: string): string[] {
  return [
    ...new Set<string>(chart.stats.map(({ statKey }: { statKey: string }) => statKey)),
    ...(selectedChart === 'ecpc' ? ['clicks', 'media_fee'] : []),
    ...(selectedChart === 'ecpe' ? ['engagements', 'media_fee'] : []),
    ...(selectedChart === 'ecppv' ? ['pages_viewed', 'media_fee'] : []),
    ...(selectedChart === 'bidMultiplier' ? ['playouts', 'impressions'] : []),
    ...(selectedChart === 'footfallAttributionRate' ? ['footfall_attribution'] : []),
    ...(selectedChart === 'driveToStoreUpliftRate' ? ['fa_venue_uplift'] : []),
    ...(selectedChart === 'visitBounceRate' ? ['clicks', 'visit', 'visit_rate'] : []),
    ...(selectedChart === 'dwellTimeChart' ? [...DWELL_TIME_KEYS, ...DWELL_TIME_ACC_KEYS] : [])
  ];
}

type State = {
  charts?: Record<string, any>;
  selectedChart: string;
  tabsConfig?: { id: string; title: string }[];
};

export function ReportingProgress() {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();

  const campaigns: Record<string, any>[] = useSelector(getSelectedCampaigns);
  const currency = useSelector(store => getCurrency(store));
  const baseCurrency = useSelector(getBaseCurrencyName);
  // @ts-ignore
  const currencyInfo = useSelector(store => getBaseCurrency(store, currency));
  const changeLogData: Record<string, any>[] = useSelector(getChangeLogData);
  const chartTimeStep = useSelector(store => getChartTimeStep(store));
  const hasAppInstalls = useSelector(getHasAppsInstall);
  const hasAudio = useSelector(getHasAudio);
  const hasDigitalVisits = useSelector(getHasDigitalConversions);
  const hasFootfallAttribution = useSelector(getHasFootfallAttribution);
  const hasLumenAttention = useSelector(getHasLumenAttention);
  const hasVideo = useSelector(getHasVideo);
  const hasViewability = useSelector(getHasViewability);
  const hasBaseline = useSelector(getHasBaseline);
  const isAgeAndGenderVisible = useSelector(getIsAgeAndGenderVisible);
  const reportingType = useSelector(getReportingType);
  const useBackups = useSelector(getStaticMode);
  const hasHouseHoldImpactsEnabled = useSelector(includeHouseHoldImpacts);
  // @ts-ignore
  const aggregatedStats: Record<string, any> = useSelector(getAggregatedStats);
  const user = useSelector(getCurrentUser);
  const userRoles = useSelector(getUserRoles);
  const isDooh = isDOOHCampaign(reportingType);

  const getDefaultSelected = () => {
    if (checkUserResourcePermissions('reportingKPIsGraphs', user, userRoles)) {
      return 'impressionsKpisChart';
    }
    return 'impressions';
  };

  const reportingKpisIncluded: Record<string, boolean> = {
    hasAppInstalls,
    hasAudio,
    hasBaseline,
    hasDigitalVisits,
    hasFootfallAttribution,
    hasLumenAttention,
    hasVideo,
    hasViewability,
    isAgeAndGenderVisible
  };

  const availableCharts = getDefaultReportingCharts(
    aggregatedStats,
    reportingType,
    reportingKpisIncluded,
    useBackups,
    hasHouseHoldImpactsEnabled,
    user,
    userRoles,
    campaigns
  );

  const getChartTitleMetricKey = (chartKey: string): string => {
    if (chartKey === 'impressionsKpisChart' && isDooh) {
      return 'impressionsKpisChartDooh';
    }
    if (chartKey === 'driveToStoreVisits') {
      return 'fa_venue_uplift';
    }
    if (chartKey === 'driveToStoreUpliftRate') {
      return 'fa_venue_uplift_pct';
    }
    return chartKey;
  };

  const statsTokens: Record<string, any> = Object.keys(availableCharts).reduce(
    (acc, availableChart) => ({
      ...acc,
      [availableChart]: {}
    }),
    {}
  );
  const [{ charts, selectedChart, tabsConfig }, setState] = useState<State>({
    charts: availableCharts,
    selectedChart: getDefaultSelected(),
    tabsConfig: Object.entries(availableCharts)
      .sort((a, b) => a[1].order - b[1].order)
      .map(([chartKey]) => ({
        id: chartKey,
        title: formatMessage({
          id: `reporting.metrics.${getChartTitleMetricKey(chartKey)}`
        })
      }))
  });

  // @ts-ignore
  const stats: Record<string, any>[] = useSelector(store => getReportingProgressStats(store, selectedChart));
  // @ts-ignore
  const baselineStats: Record<string, any>[] = useSelector(store => getReportingProgressStats(store, 'baseline'));
  const variableType = useSelector(store => getVariableType(store));
  const variableValues = useSelector(store => getVariableValues(store));
  const variable = variableType && variableValues.length ? { [variableType]: variableValues } : undefined;
  const { beginDate, endDate, hasCustomDates } = useSelector(getTimeFrame);
  const timezone = useSelector(getTimeZone);
  const generalCartographyCountries = useSelector(getCartographyCountries);
  const generalCartographyLevel = useSelector(getCartographyLevel);
  const generalCartographyLevelIds = useSelector(getCartographyLevelIds);
  // @ts-ignore
  const cartographyFilter: Record<string, string[]> = getCartographyFilter({
    cartographyCountries: generalCartographyCountries,
    cartographyLevel: generalCartographyLevel,
    cartographyLevelIds: generalCartographyLevelIds
  });
  const formattedTimeFrame = formatTimeFrame(
    {
      end: endDate,
      start: beginDate
    },
    timezone,
    undefined,
    undefined,
    hasCustomDates,
    selectedChart === 'storeVisits' && hasFootfallAttribution
  );
  const dateInterval = {
    ...formattedTimeFrame,
    timezone
  };

  useEffect(() => {
    return () => {
      Object.values(statsTokens).forEach(token => {
        token?.cancel?.();
      });
    };
  }, []);

  const requestBody = getProgressStatsRequestBody(
    {
      baseCurrency,
      currency
    },
    dateInterval,
    chartTimeStep,
    getOutputMetricsForSelectedChart(charts![selectedChart], selectedChart),
    cartographyFilter,
    variable,
    useBackups
  );

  const fetchStats = async () => {
    if (charts && selectedChart && !stats) {
      const campaignIds = uniq(campaigns.map(campaign => campaign.id));
      const [mainStats, secondaryStats] = await fetchProgressStats(
        user.id,
        campaignIds,
        chartTimeStep,
        selectedChart,
        requestBody,
        useBackups,
        statsTokens[selectedChart]
      );
      dispatch(reportingProgressSetStats(selectedChart, mainStats));
      if (secondaryStats) {
        dispatch(reportingProgressSetStats('baseline', secondaryStats));
      }
    }
  };

  useDidUpdate(() => {
    if (selectedChart && !['ageAndGender', 'impressionsKpisChart'].includes(selectedChart)) {
      fetchStats();
    }
  }, [selectedChart]);

  const getChangeLogDataGrouped = () => {
    changeLogData.forEach(data => {
      data.specificDate = getDateFormatted(chartTimeStep, data.timestamp, false);
    });
    return groupBy(changeLogData, 'specificDate');
  };

  const getStatsDataSorted = (statsData: Record<string, any>[]) => {
    return statsData
      ? [...statsData].sort((a, b) => {
          a = new Date(a[chartTimeStep]);
          b = new Date(b[chartTimeStep]);
          return a > b ? 1 : a < b ? -1 : 0;
        })
      : [];
  };

  const getXAxisForDwellTimeChart = () => {
    const changeLogPlotLines: React.ComponentProps<typeof ReportingChart>['changeLogPlotLines'] = [];
    const xCategories: string[] = [];
    if (stats) {
      DWELL_TIME_KEYS.forEach(dwellTimeKey => {
        const key = dwellTimeKey.split('_');
        key[0] === 'dwellTime' && xCategories.push(key[1]);
      });
    }
    return {
      changeLogPlotLines,
      xCategories
    };
  };

  const getXAxisForTimeStepCharts = () => {
    const changeLogPlotLines: React.ComponentProps<typeof ReportingChart>['changeLogPlotLines'] = [];
    const groupedChangeLogData = getChangeLogDataGrouped();
    const statsData = getStatsDataSorted(cloneDeep(stats));
    const xCategories: string[] = [];
    statsData.forEach((statData, i) => {
      xCategories.push(getDateFormatted(chartTimeStep, statData[chartTimeStep], statsData.length > 40));
      if (checkUserResourcePermissions('reportingCampaignChangeLog', user, userRoles)) {
        Object.keys(groupedChangeLogData).forEach((changeLogKey, changeLogIndex) => {
          if (changeLogKey === getDateFormatted(chartTimeStep, statData[chartTimeStep])) {
            changeLogPlotLines.push({
              className: 'changeLogBar',
              color: '#ddd',
              dashStyle: 'shortdash',
              events: {
                click: () => dispatch(reportingTrigger('chartChangeLogClick', { changeLogIndex }))
              },
              value: i,
              width: 5
            });
          }
        });
      }
    });
    return {
      changeLogPlotLines,
      xCategories
    };
  };

  const getXAxis = () => {
    if (selectedChart === 'dwellTimeChart') {
      return getXAxisForDwellTimeChart();
    }
    return getXAxisForTimeStepCharts();
  };

  const getDwellTimeData = (statKey: string) => {
    const dwellTimeData: Record<string, any>[] = [];
    Object.entries(stats[0]).forEach(([dwellTimeKey, data]) => {
      const key = dwellTimeKey.split('_')[0];
      if (key === statKey) {
        dwellTimeData.push(data);
      }
    });
    return dwellTimeData;
  };

  const getSeriesData = useCallback(
    (statKey: string): any[] => {
      if (selectedChart === 'dwellTimeChart' && ['dwellTimeAcc', 'dwellTime'].includes(statKey)) {
        return getDwellTimeData(statKey);
      }
      return getStatsDataSorted(cloneDeep(stats)).map(statsEntry => {
        if (baselineStats && ['proximity_visits', 'organic_traffic'].includes(statKey)) {
          const baselineDateStats = baselineStats.find(({ sdate }) => {
            return sdate === statsEntry[chartTimeStep];
          });
          return baselineDateStats ? baselineDateStats[statKey] : 0;
        }
        return statsEntry[statKey] || 0;
      });
    },
    [baselineStats, stats]
  );

  const getCalculatedMetrics = (statKey: string) => {
    const currencyInfoWithCountry = {
      ...currencyInfo,
      countries: [user.country]
    };
    let metrics = {
      avg: '0.00',
      max: '0.00',
      min: '0.00'
    };
    if (stats) {
      const chartStats = getSeriesData(statKey);
      metrics = {
        avg: (chartStats.reduce((acc, statsEntry) => acc + statsEntry, 0) / chartStats.length).toFixed(2),
        max: Math.max(...chartStats).toFixed(2),
        min: Math.min(...chartStats).toFixed(2)
      };
    }
    const formatOutput = (value: string): string => {
      if (value === '0.00') {
        return 'N/A';
      }
      return formatNumber(statKey, value, currencyInfoWithCountry);
    };
    return {
      avg: formatOutput(metrics.avg),
      max: formatOutput(metrics.max),
      min: formatOutput(metrics.min)
    };
  };

  const tabContent = (id: string) => {
    const chart = charts?.[id];
    let changeLogPlotLines: React.ComponentProps<typeof ReportingChart>['changeLogPlotLines'] = [];
    let xCategories: string[] = [];
    if (stats) {
      ({ changeLogPlotLines, xCategories } = getXAxis());
    }
    const calculatedMetrics = getCalculatedMetrics(chart.stats[0].statKey);
    return (
      <div className="reportingCharts__tabContent">
        <div className="chartWrapper">
          {!['ageAndGender', 'impressionsKpisChart'].includes(id) ? (
            <ReportingChart
              changeLogPlotLines={changeLogPlotLines}
              chart={chart}
              getSeriesData={getSeriesData}
              selectedChart={id}
              xCategories={xCategories}
              {...getTooltipConfig(currencyInfo, user.country)}
            />
          ) : id === 'ageAndGender' ? (
            <ReportingAgeAndGender />
          ) : (
            <ImpressionsKpisChart />
          )}
        </div>
        {id !== 'impressionsKpisChart' && (
          <>
            <hr />
            <div className="chartFooterContainer">
              <div className="calculatedMetricsContainer">
                <label className="calculatedMetric avg">
                  {formatMessage(
                    {
                      defaultMessage: 'Average Value: ',
                      id: 'reporting.reportingCharts.calculatedMetrics.avg'
                    },
                    {
                      stat: formatMessage({ id: `reporting.metrics.${chart.stats[0].statKey}` }),
                      value: calculatedMetrics.avg
                    }
                  )}
                </label>
                <label className="calculatedMetric max">
                  {formatMessage(
                    {
                      defaultMessage: 'Max Value: ',
                      id: 'reporting.reportingCharts.calculatedMetrics.max'
                    },
                    {
                      stat: formatMessage({ id: `reporting.metrics.${chart.stats[0].statKey}` }),
                      value: calculatedMetrics.max
                    }
                  )}
                </label>
                <label className="calculatedMetric min">
                  {formatMessage(
                    {
                      defaultMessage: 'Min Value: ',
                      id: 'reporting.reportingCharts.calculatedMetrics.min'
                    },
                    {
                      stat: formatMessage({ id: `reporting.metrics.${chart.stats[0].statKey}` }),
                      value: calculatedMetrics.min
                    }
                  )}
                </label>
              </div>
              <div className="changeLogButtonWrapper">
                <button
                  className="changeLogButton primary"
                  onClick={() => reportingTrigger('seeAllChangeLog')}
                  type="button"
                >
                  {formatMessage({
                    defaultMessage: 'FullChangeLog',
                    id: 'reporting.reportingCharts.changeLogButton'
                  })}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    );
  };

  const formatTabs: TabsItemProps[] =
    tabsConfig?.map((config: TabsItemProps) => ({
      ...config,
      component: tabContent(config.id)
    })) || [];

  return (
    <>
      <div
        className={`reportingShortcuts__element--${campaigns.length > 1 ? 'multi' : 'single'}`}
        id="reportingCharts"
      />
      <SectionHeader className="reportingCharts__sectionHeader" hideAdvancedFilters section="campaignsProgress">
        <div className="h2 fontRegular reportingCharts__title">
          {formatMessage({
            defaultMessage: 'Campaign progress',
            id: 'reporting.reportingCharts.title'
          })}
        </div>
      </SectionHeader>
      <div className="reportingCharts">
        <Tabs
          onChangeTab={(tabId: string) =>
            setState(prevState => ({
              ...prevState,
              selectedChart: tabId
            }))
          }
          tabs={formatTabs}
        />
      </div>
    </>
  );
}
